// Draw.js — نسخة مع عدسة تكبير للموبايل (Long-Press Magnifier)

import OLDraw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import Text from 'ol/style/Text';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { distance } from 'ol/coordinate';
import { map } from './Map';
import { setFloatingControlsVisible, closeSidebarOnMobile } from './CustomFunctions';
import { snapSource } from './Snapping';

/* =========================
   طبقة الرسم + تفاعلات تعديل/التقاط
   ========================= */
const drawSource = new VectorSource();
const drawLayer = new VectorLayer({ source: drawSource, name: 'DrawLayer' });
map.addLayer(drawLayer);

let drawInteraction = null;
// Snap interaction تلقائي على الأجزاء المرسومة - يعمل دائماً على vertex فقط
const snapInteraction = new Snap({
  source: drawSource,
  pixelTolerance: 15,
  edge: false,  // تعطيل التجاذب على الأضلاع
  vertex: true  // تفعيل التجاذب فقط على النقاط
});
map.addInteraction(snapInteraction);

// Snap interaction إضافي على الأجزاء المقاسة (cross-snapping)
// ليعمل snap على الأجزاء المقاسة عند الرسم - يعمل دائماً
let drawSnapInteractionMeasure = null;
function initDrawSnapToMeasure() {
  // الحصول على measure source
  if (typeof window.measureSourceForSnap === 'function') {
    const measureSrc = window.measureSourceForSnap();
    if (measureSrc) {
      if (drawSnapInteractionMeasure) {
        map.removeInteraction(drawSnapInteractionMeasure);
      }
      drawSnapInteractionMeasure = new Snap({
        source: measureSrc,
        pixelTolerance: 15,
        edge: false,  // تعطيل التجاذب على الأضلاع
        vertex: true  // تفعيل التجاذب فقط على النقاط
      });
      map.addInteraction(drawSnapInteractionMeasure);
    }
  }
}

// تهيئة cross-snap عند بدء الرسم
function ensureDrawCrossSnap() {
  if (!drawSnapInteractionMeasure) {
    initDrawSnapToMeasure();
  }
}

// جعل drawSource متاحاً عالمياً للـ cross-snap
window.drawSourceForSnap = () => drawSource;

// ⚡ [LATE-BINDING] - Snapping will resolve its source on-demand during activation

/* =========================
   حالة عامة
   ========================= */
let drawnShapes = [];
let pendingTextFeature = null;
window.GeoPST.state.isDrawing = false;
let isDrawing = false;

/* =========================
   عدسة تكبير للموبايل (Canvas-based) — معزولة عن عدسة القياس
   ========================= */
const DRAW_MAGNIFIER_SIZE = 90;       // px
const DRAW_LONG_PRESS_MS = 450;       // مدة الضغط الطويل

let drawMagnifier = null;
let drawMagnifierActive = false;
let drawLongPressTimer = null;
let drawRafId = null;
let _drawMoveEndHandler = null;
let _drawPostRenderHandler = null;

function createDrawMagnifier() {
  // افحص إذا كان المؤشر موجوداً بالفعل
  let existing = document.getElementById('map-magnifier');
  if (existing) {
    drawMagnifier = existing;
    return;
  }

  drawMagnifier = document.createElement('div');
  drawMagnifier.id = 'map-magnifier';
  Object.assign(drawMagnifier.style, {
    width: DRAW_MAGNIFIER_SIZE + 'px',
    height: DRAW_MAGNIFIER_SIZE + 'px',
    zIndex: 1900,
    display: 'none'
  });

  // علامة تصويب هندسية دقيقة
  if (!drawMagnifier.querySelector('.magnifier-crosshair')) {
    const cross = document.createElement('div');
    cross.className = 'magnifier-crosshair';
    
    const centerDot = document.createElement('div');
    centerDot.className = 'magnifier-center-dot';
    
    drawMagnifier.appendChild(cross);
    drawMagnifier.appendChild(centerDot);
  }

  // مؤشر السناب (الدائرة البرتقالية)
  if (!drawMagnifier.querySelector('#map-snap-indicator')) {
    const snapCircle = document.createElement('div');
    snapCircle.id = 'map-snap-indicator';
    Object.assign(snapCircle.style, {
      position: 'absolute',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: 'rgba(255, 140, 0, 0.8)',
      border: '2px solid #fff',
      display: 'none',
      zIndex: 3,
      transform: 'translate(-50%, -50%)'
    });
    drawMagnifier.appendChild(snapCircle);
  }

  document.body.appendChild(drawMagnifier);
}

// متغيرات كاش للأداء
let cachedViewport = null;
let cachedMapCanvas = null;
let drawMagnifierRenderPending = false;
let drawMagnifierLastPixel = null;

function showDrawMagnifierAt(pixel) {
  if (!drawMagnifier) createDrawMagnifier();
  drawMagnifierLastPixel = pixel;
  if (!drawMagnifierRenderPending) {
    drawMagnifierRenderPending = true;
    requestAnimationFrame(renderDrawMagnifier);
  }
}

// متغيرات السناب (المغناطيس) للرسم
let currentDrawSnappedCoord = null;
const DRAW_SNAP_THRESHOLD = 20; // بكسل

/**
 * الحصول على أقرب vertex (نقطة رأس) فقط من الشكل
 * @param {ol.geom.Geometry} geometry - الشكل الهندسي
 * @param {ol.Coordinate} coordinate - الإحداثيات المستهدفة
 * @returns {ol.Coordinate|null} أقرب vertex أو null
 */
function getClosestVertex(geometry, coordinate) {
  if (!geometry || !coordinate) return null;
  if (geometry.getType() === 'Point') return geometry.getCoordinates();

  const flats = geometry.getFlatCoordinates ? geometry.getFlatCoordinates() : null;
  const stride = geometry.getStride ? geometry.getStride() : null;
  if (!flats || !stride) return geometry.getClosestPoint(coordinate);

  let closest = null;
  let minDistSq = Infinity;
  const cx = coordinate[0], cy = coordinate[1];

  for (let i = 0; i < flats.length; i += stride) {
    const vx = flats[i];
    const vy = flats[i + 1];
    const dx = vx - cx;
    const dy = vy - cy;
    const distSq = dx * dx + dy * dy;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      closest = [vx, vy];
    }
  }
  return closest;
}

function renderDrawMagnifier() {
  drawMagnifierRenderPending = false;
  if (typeof isDrawing !== 'undefined' && !isDrawing) { hideDrawMagnifier(); return; }

  const pixel = drawMagnifierLastPixel;
  if (!pixel || !drawMagnifier) return;

  cachedViewport = map.getViewport();
  const mapRect = cachedViewport.getBoundingClientRect();
  const clientX = mapRect.left + pixel[0];
  const clientY = mapRect.top + pixel[1];

  // وضع المؤشر
  drawMagnifier.style.transform = `translate(-50%, -50%)`;
  drawMagnifier.style.left = `${clientX}px`;
  drawMagnifier.style.top = `${clientY}px`;
  drawMagnifier.style.display = 'block';



  // ─────────────────────────────────────────────────────────────
  // منطق السناب (المغناطيس)
  // ─────────────────────────────────────────────────────────────
  currentDrawSnappedCoord = null;
  let snapPixel = null;
  const snapIndicator = document.getElementById('map-snap-indicator');

  if (window.isSnappingEnabled) {
    const res = map.getView().getResolution();
    if (res <= (window.MAX_SNAP_RESOLUTION || 0.28)) {
      const mapCenterCoord = map.getCoordinateFromPixel(pixel);
      let closestPoint = null;
      let minDist = Infinity;

      if (mapCenterCoord) {
        const snapThreshold = (typeof DRAW_SNAP_THRESHOLD !== 'undefined' ? DRAW_SNAP_THRESHOLD : 20);
        const buffer = res * snapThreshold;
        const searchExtent = [
          mapCenterCoord[0] - buffer, mapCenterCoord[1] - buffer,
          mapCenterCoord[0] + buffer, mapCenterCoord[1] + buffer
        ];

        const findInSource = (src) => {
          if (!src) return;
          const featuresInBox = src.getFeaturesInExtent(searchExtent);
          for (let i = 0; i < featuresInBox.length; i++) {
            const f = featuresInBox[i];
            if (f.getGeometry().getType() === 'Point' && f.get('text')) continue;
            const v = getClosestVertex(f.getGeometry(), mapCenterCoord);
            if (v) {
              const dx = v[0] - mapCenterCoord[0];
              const dy = v[1] - mapCenterCoord[1];
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d < minDist) {
                minDist = d;
                closestPoint = v;
              }
            }
          }
        };

        findInSource(snapSource);
        findInSource(drawSource);
        if (typeof window.measureSourceForSnap === 'function') findInSource(window.measureSourceForSnap());

        if (closestPoint) {
          const closestPixel = map.getPixelFromCoordinate(closestPoint);
          const dx = closestPixel[0] - pixel[0];
          const dy = closestPixel[1] - pixel[1];
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist <= snapThreshold) {
            currentDrawSnappedCoord = closestPoint;
            snapPixel = closestPixel;
            if (!window.lastDrawSnapState && navigator.vibrate) navigator.vibrate(15);
            window.lastDrawSnapState = true;
          } else {
            window.lastDrawSnapState = false;
          }
        } else {
          window.lastDrawSnapState = false;
        }
      } else {
        window.lastDrawSnapState = false;
      }
    } else {
      window.lastDrawSnapState = false;
    }
  }

  // تحديث موقع مؤشر السناب
  if (snapIndicator) {
    if (snapPixel) {
      const dx = snapPixel[0] - pixel[0];
      const dy = snapPixel[1] - pixel[1];
      snapIndicator.style.left = (DRAW_MAGNIFIER_SIZE / 2 + dx) + 'px';
      snapIndicator.style.top = (DRAW_MAGNIFIER_SIZE / 2 + dy) + 'px';
      snapIndicator.style.display = 'block';
    } else {
      snapIndicator.style.display = 'none';
    }
  }

  drawMagnifierActive = true;
}

function hideDrawMagnifier() {
  if (!drawMagnifier) return;
  drawMagnifier.style.display = 'none';
  drawMagnifierActive = false;
}

const isMobile = () => window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

/* ربط أحداث اللمس على الـ viewport للرسم فقط */
(function attachDrawMagnifierHandlers() {
  const viewport = map && map.getViewport ? map.getViewport() : null;
  if (!viewport) return;
  if (viewport._drawMagnifierBound) return;
  viewport._drawMagnifierBound = true;

  viewport.addEventListener('touchstart', (ev) => {
    if (!isDrawing || isMobile()) return;                 // العدسة للرسم فقط
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0];
    const r = viewport.getBoundingClientRect();
    const px = [t.clientX - r.left, t.clientY - r.top];
    drawLongPressTimer = setTimeout(() => { showDrawMagnifierAt(px); }, DRAW_LONG_PRESS_MS);
  }, { passive: true });

  viewport.addEventListener('touchmove', (ev) => {
    if (drawLongPressTimer) { clearTimeout(drawLongPressTimer); drawLongPressTimer = null; }
    if (!drawMagnifierActive || isMobile() || ev.touches.length !== 1) return;
    const t = ev.touches[0];
    const r = viewport.getBoundingClientRect();
    const px = [t.clientX - r.left, t.clientY - r.top];
    showDrawMagnifierAt(px); // تحديث مستمر لموضع/محتوى العدسة
  }, { passive: true });

  viewport.addEventListener('touchend', () => {
    if (drawLongPressTimer) { clearTimeout(drawLongPressTimer); drawLongPressTimer = null; }
    // على الجوال، لا نخفي العدسة أبداً لأنها تعمل في المنتصف وثابتة
    if (!isMobile()) hideDrawMagnifier();
  }, { passive: true });
  // ⚡ Fix: Misunderstanding between Lens & Search Field
  const $searchInp = $('#mobileSearchInput');
  if ($searchInp.length) {
    $searchInp.on('focus', () => {
      window.magnifierSuppressed = true;
      hideDrawMagnifier();
    });
    $searchInp.on('blur', () => {
      setTimeout(() => {
        window.magnifierSuppressed = false;
        if (isDrawing && isMobile()) updateCenterMagnifier();
      }, 300);
    });
  }
})();

// وظائف التحكم بالجوال
function setupMobileHandlers() {
  $('#btnMobileAddPoint').off('click').on('click', () => {
    if (!isDrawing) return;

    // استخدام النقطة الملقوطة (Snapped) إذا وجدت، وإلا استخدام مركز الخريطة
    const center = currentDrawSnappedCoord ? currentDrawSnappedCoord : map.getView().getCenter();
    const strokeColor = document.getElementById('strokeColor')?.value || '#dc3545';
    const fillColor = document.getElementById('fillColor')?.value || '#dc3545';
    const strokeWidth = parseInt(document.getElementById('strokeWidth')?.value || '2');

    // إذا كان رسم نقطة
    if (currentDrawType === 'Point' || currentDrawType === 'Text') {
      const feature = new Feature(new Point(center));
      const id = Date.now();
      feature.setId(id);

      const style = new Style({
        image: new CircleStyle({ radius: 6, fill: new Fill({ color: strokeColor }), stroke: new Stroke({ color: '#fff', width: 2 }) })
      });
      feature.setStyle(style);

      drawSource.addFeature(feature);
      drawnShapes.push({ id, feature, type: currentDrawType === 'Text' ? 'نص' : 'نقطة' });
      updateDrawingsList();

      if (currentDrawType === 'Text') showTextModal(feature);
    }
    // إذا كان مضلع أو خط
    else if (drawInteraction && drawInteraction.appendCoordinates) {
      drawInteraction.appendCoordinates([center]);
    }

    if (navigator.vibrate) navigator.vibrate(25);
  });

  $('#btnMobileDone').off('click').on('click', () => {
    if (drawInteraction) drawInteraction.finishDrawing();
    stopDrawing($('#draw'));
    toggleMobileControls(false);
  });
}


function toggleMobileControls(show, type) {
  const controls = $('#mobileToolControls');
  if (show && isMobile()) {
    const isFree = type && (type.includes('Free') || type === 'Text');
    if (isFree) {
      controls.fadeOut(200);
      hideDrawMagnifier(); // إخفاء العدسة فوراً في الأنماط الحرة أو النص
      return;
    }
    controls.fadeIn(300);
    setupMobileHandlers();
    $('#mobileToolValue').text('--');
    $('.tool-label').text('رسم: ' + getShapeTypeInArabic(type));
  } else {
    controls.fadeOut(300);
  }
}

let lastDrawMagnifierUpdateTime = 0;
function updateCenterMagnifier() {
  if (!isDrawing || !isMobile() || (currentDrawType && (currentDrawType.includes('Free') || currentDrawType === 'Text'))) return;

  // ⚡ Throttle postrender updates to ~30fps
  const now = performance.now();
  if (now - lastDrawMagnifierUpdateTime < 32) return;
  lastDrawMagnifierUpdateTime = now;

  // لا نظهر العدسة إذا كانت ممنوعة (الشريط الجانبي مفتوح)
  if (window.magnifierSuppressed) return;

  const size = map.getSize();
  if (size) {
    const centerPx = [size[0] / 2, size[1] / 2];
    showDrawMagnifierAt(centerPx);
    if (drawMagnifier) {
      drawMagnifier.style.display = 'block';
      drawMagnifierActive = true;
    }
  }
}

let currentDrawType = null;

/* =========================
   زر "إظهار أداة الرسم" العائم
   ========================= */
(function ensureDrawShowChip() {
  if ($('#drawShowChip').length) return;
  const chip = $(`
    <div id="drawShowChip">
      <button type="button" id="drawShowChipBtn">
        <i class="fas fa-pencil-alt"></i> إظهار أداة الرسم
      </button>
    </div>
  `);
  $('body').append(chip);
  $('#drawShowChipBtn').on('click', () => { $('#drawModal').css('display', 'flex'); $('#drawShowChip').hide(); });
})();

/* =========================
   تصغير مودال الرسم - الأنماط الآن في components.css
   ========================= */

/* =========================
   زر الرسم بالشريط الجانبي
   ========================= */
$('#draw').off('click').on('click', function (e) {
  e.preventDefault();
  const button = $(this);
  const section = $('#drawSection');
  if (!isDrawing) {
    startDrawing(button);
    section.slideDown(200);
  } else {
    stopDrawing(button);
    section.slideUp(200);
  }
});

/* =========================
   تشغيل/إيقاف الرسم
   ========================= */
function startDrawing(button) {
  isDrawing = true; window.GeoPST.state.isDrawing = true;
  button.addClass('active'); button.find('span').text('إيقاف الرسم');
  button.find('i').removeClass('fa-pencil-alt').addClass('fa-times');

  // إخفاء عدسة القياس عند بدء الرسم لمنع التداخل
  const mapMagnifier = document.getElementById('map-magnifier');
  if (mapMagnifier) mapMagnifier.style.display = 'none';

  showDrawPanel();

  // التأكد من تفعيل cross-snap على الأجزاء المقاسة
  ensureDrawCrossSnap();

  // إيقاف تتبع الموقع (Follow) عند بدء الرسم لمنع تداخل حركة الخريطة
  if (window.GeoPST_Geolocation && typeof window.GeoPST_Geolocation.stopFollowing === 'function') {
    window.GeoPST_Geolocation.stopFollowing();
  }

  // No separate sidebar manipulation needed here

}

function stopDrawing(button) {
  isDrawing = false; window.GeoPST.state.isDrawing = false;
  button.removeClass('active'); button.find('span').text('الرسم');
  button.find('i').removeClass('fa-times').addClass('fa-pencil-alt');
  hideDrawPanel();
  removeDrawInteraction();
  hideDrawMagnifier();

  if (isMobile()) {
    toggleMobileControls(false);
    if (_drawMoveEndHandler) map.un('moveend', _drawMoveEndHandler);
    if (_drawPostRenderHandler) map.un('postrender', _drawPostRenderHandler);
    _drawMoveEndHandler = null;
    _drawPostRenderHandler = null;


    // استعادة الأزرار العائمة فقط إذا الشريط الجانبي مغلق
    if (!$('#sidebar').hasClass('open')) {
      setFloatingControlsVisible(true);
    }

    // No separate sidebar manipulation needed here

  }
}

function cleanupMobileDrawMode() {
  if (_drawMoveEndHandler) {
    map.un('moveend', _drawMoveEndHandler);
    _drawMoveEndHandler = null;
  }
  if (_drawPostRenderHandler) {
    map.un('postrender', _drawPostRenderHandler);
    _drawPostRenderHandler = null;
  }
  hideDrawMagnifier();
  toggleMobileControls(false);
  window.magnifierSuppressed = false;
}

window.addEventListener('resize', () => {
  if (!isMobile()) {
    cleanupMobileDrawMode();
  }
});

/* إغلاق تام */
function closeDrawCompletely() {
  isDrawing = false; window.GeoPST.state.isDrawing = false;
  const btn = $('#draw');
  btn.removeClass('active'); btn.find('span').text('الرسم');
  btn.find('i').removeClass('fa-times').addClass('fa-pencil-alt');
  removeDrawInteraction();
  $('#drawModal').hide();
  $('#drawShowChip').hide();
  hideDrawMagnifier();
}

/* =========================
   مودال أداة الرسم
   ========================= */
function ensureDrawModal() {
  if ($('#drawModal').length) return;

  const modalHtml = `
    <div id="drawModal" class="coordinate-modal" style="display:none" aria-modal="true" role="dialog">
      <div class="coordinate-modal-content">
        <div class="coordinate-modal-header">
          <h4><i class="fas fa-pencil-ruler"></i> أدوات الرسم</h4>
          <button class="coordinate-modal-close" id="drawModalClose" aria-label="إغلاق">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="coordinate-modal-body">
          <div id="drawModalContent"></div>
        </div>
        <div class="coordinate-modal-footer">
          <button type="button" class="coordinate-btn coordinate-btn-secondary" id="drawModalHide">
            <i class="fas fa-eye-slash"></i> إخفاء
          </button>
        </div>
      </div>
    </div>
  `;
  $('body').append(modalHtml);

  $('#drawModalClose').off('click').on('click', closeDrawCompletely);
  $('#drawModalHide').off('click').on('click', hideDrawPanel);
  $('#drawModal').off('click').on('click', function (e) {
    if (e.target === this) hideDrawPanel();
  });
}

function showDrawPanel() {
  // ensureDrawModal();

  // إذا القياس شغّال، يتم التعامل معه مركزيًا عبر ToolsHub

  const strokeColor = localStorage.getItem('strokeColor') || '#dc3545';
  const fillColor = localStorage.getItem('fillColor') || '#dc3545';
  const strokeWidth = localStorage.getItem('strokeWidth') || '2';

  const panelHtml = `
    <div style="text-align:right;direction:rtl;">
      <div class="draw-control-group">
        <div class="section-header">
          <span>إعدادات الرسم</span>
          <i class="fas fa-palette"></i>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px;">
          <div>
            <label class="control-label"><i class="fas fa-border-style" style="margin-left:4px;"></i> لون الحدود</label>
            <div class="modern-color-input">
              <input type="color" id="strokeColor" value="${strokeColor}" />
              <div class="color-display" id="strokeDisplay" style="background-color:${strokeColor};">${strokeColor.toUpperCase()}</div>
            </div>
          </div>
          <div>
            <label class="control-label"><i class="fas fa-fill-drip" style="margin-left:4px;"></i> لون التعبئة</label>
            <div class="modern-color-input">
              <input type="color" id="fillColor" value="${fillColor}" />
              <div class="color-display" id="fillDisplay" style="background-color:${fillColor};">${fillColor.toUpperCase()}</div>
            </div>
          </div>
        </div>

        <div>
          <label class="control-label"><i class="fas fa-weight" style="margin-left:4px;"></i> سماكة الخط</label>
          <input type="range" id="strokeWidth" class="modern-range-input" min="1" max="10" value="${strokeWidth}" />
          <div class="thickness-display">
            <span style="font-size:11px;color:#666;">رفيع</span>
            <span class="thickness-value" id="thicknessValue">${strokeWidth}px</span>
            <span style="font-size:11px;color:#666;">سميك</span>
          </div>
        </div>

        <div id="stylePreview" class="style-preview-box"></div>
      </div>

      <div class="draw-control-group">
        <div class="section-header">
          <span>أدوات الرسم</span>
          <i class="fas fa-draw-polygon"></i>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
          <button class="modern-draw-btn" onclick="activateDraw('Point', this)" id="drawPoint"><i class="fas fa-map-marker-alt"></i><span>نقطة</span></button>
          <button class="modern-draw-btn" onclick="activateDraw('LineString', this)" id="drawLine"><i class="fas fa-minus"></i><span>خط</span></button>
          <button class="modern-draw-btn" onclick="activateDraw('LineStringFree', this)" id="drawLineFree"><i class="fas fa-pen"></i><span>خط حر</span></button>
          <button class="modern-draw-btn" onclick="activateDraw('Polygon', this)" id="drawPolygon"><i class="fas fa-draw-polygon"></i><span>مضلع</span></button>
          <button class="modern-draw-btn" onclick="activateDraw('PolygonFree', this)" id="drawPolygonFree"><i class="fas fa-vector-square"></i><span>مضلع حر</span></button>
          <button class="modern-draw-btn" onclick="activateDraw('Text', this)" id="drawText"><i class="fas fa-font"></i><span>نص</span></button>
        </div>
      </div>

      <div class="draw-control-group">
        <div class="section-header">
          <span>الأشكال المرسومة</span>
          <i class="fas fa-list-ul"></i>
        </div>
        <div class="drawings-container" id="drawingsList">
          <div style="text-align:center;color:#6c757d;padding:14px;">
            <i class="fas fa-draw-polygon" style="font-size:1.6rem;margin-bottom:8px;opacity:.5;"></i>
            <p style="margin:0;font-size:.8rem;">لا توجد رسومات بعد.</p>
          </div>
        </div>
      </div>

      <div class="control-buttons">
        <button class="control-btn clear-btn" id="clearDrawings"><i class="fas fa-trash-alt"></i><span>مسح الكل</span></button>
        <button class="control-btn export-btn" id="exportDrawings"><i class="fas fa-download"></i><span>تصدير</span></button>
      </div>
    </div>
  `;

  $('#drawSectionContent').html(panelHtml);

  $('#clearDrawings').off('click').on('click', clearDrawings);
  $('#exportDrawings').off('click').on('click', exportDrawings);

  ['strokeColor', 'fillColor', 'strokeWidth'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('input', updatePreviewAndSave);
  });

  updatePreviewAndSave();
  updateDrawingsList();
}

function hideDrawPanel() {
  $('#drawSection').slideUp(200);
}

/* =========================
   إدارة التفاعل
   ========================= */
window.activateDraw = function (type, btnElement) { addDrawInteraction(type, btnElement); };

function addDrawInteraction(type, btnElement) {
  removeDrawInteraction();

  const strokeColor = document.getElementById('strokeColor')?.value || '#dc3545';
  const fillColor = document.getElementById('fillColor')?.value || '#dc3545';
  const strokeWidth = parseInt(document.getElementById('strokeWidth')?.value || '2');

  let drawType = type, freehand = false;
  if (type === 'LineStringFree') { drawType = 'LineString'; freehand = true; }
  if (type === 'PolygonFree') { drawType = 'Polygon'; freehand = true; }
  if (type === 'Text') { drawType = 'Point'; }

  drawInteraction = new OLDraw({
    source: drawSource,
    type: drawType,
    freehand: freehand,
    condition: (mapBrowserEvent) => {
      // على الجوال، نمنع النقر المباشر إلا إذا كان رسم حر أو نص
      if (isMobile() && !(freehand || type === 'Text')) return false;
      return true;
    },
    style: new Style({
      stroke: new Stroke({ color: strokeColor, width: strokeWidth, lineDash: [10, 10] }),
      fill: new Fill({ color: drawType === 'Polygon' ? hexToRgba(fillColor, 0.22) : 'rgba(0,0,0,0)' }),
      image: new CircleStyle({ radius: 5, fill: new Fill({ color: strokeColor }), stroke: new Stroke({ color: '#fff', width: 2 }) })
    })
  });

  map.addInteraction(drawInteraction);

  // التأكد من تفعيل cross-snap على الأجزاء المقاسة
  ensureDrawCrossSnap();

  // التأكد من أن snap interactions في الترتيب الصحيح (في النهاية)
  if (snapInteraction) {
    map.removeInteraction(snapInteraction);
    map.addInteraction(snapInteraction);
  }
  if (drawSnapInteractionMeasure) {
    map.removeInteraction(drawSnapInteractionMeasure);
    map.addInteraction(drawSnapInteractionMeasure);
  }

  if (isMobile()) {
    closeSidebarOnMobile();

    currentDrawType = type;
    toggleMobileControls(true, type);

    // Register magnifier update events so it refreshes during pan/zoom
    _drawMoveEndHandler = updateCenterMagnifier;
    _drawPostRenderHandler = updateCenterMagnifier;
    map.on('moveend', _drawMoveEndHandler);
    map.on('postrender', _drawPostRenderHandler);

    // السماح للعدسة بالظهور بعد إغلاق الشريط الجانبي (المؤقت الداخلي في المساعد يهتم به)
    updateCenterMagnifier();
  }

  // Desktop logic removed to prevent sidebar desync

  drawInteraction.on('drawend', function (evt) {
    const feature = evt.feature;
    if (type === 'Text') {
      showTextModal(feature);
    } else {
      const id = Date.now();
      feature.setId(id);
      feature.setStyle(new Style({
        stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
        fill: new Fill({ color: (type === 'Polygon' || type === 'PolygonFree') ? hexToRgba(fillColor, 0.28) : 'rgba(0,0,0,0)' }),
        image: new CircleStyle({ radius: 6, fill: new Fill({ color: strokeColor }), stroke: new Stroke({ color: '#fff', width: 2 }) })
      }));
      drawnShapes.push({ id, feature, type: getShapeTypeInArabic(type) });
      updateDrawingsList();

      // التأكد من أن cross-snap يعمل على الأجزاء المقاسة بعد إضافة جزء جديد
      ensureDrawCrossSnap();
    }
  });

  document.querySelectorAll('.modern-draw-btn').forEach(btn => btn.classList.remove('active'));
  if (btnElement) btnElement.classList.add('active');
}

function removeDrawInteraction() {
  if (drawInteraction) { map.removeInteraction(drawInteraction); drawInteraction = null; }
  document.querySelectorAll('.modern-draw-btn').forEach(btn => btn.classList.remove('active'));

  if (isMobile()) {
    toggleMobileControls(false);
    hideDrawMagnifier();

    // استعادة شريط البحث والأزرار العائمة فقط إذا الشريط الجانبي مغلق
    if (!$('#sidebar').hasClass('open')) {
      setFloatingControlsVisible(true);
    }
  }
}

/* =========================
   قائمة الرسومات
   ========================= */
function getShapeTypeInArabic(type) {
  const types = { 'Point': 'نقطة', 'LineString': 'خط', 'LineStringFree': 'خط حر', 'Polygon': 'مضلع', 'PolygonFree': 'مضلع حر', 'Text': 'نص' };
  return types[type] || type;
}

function updateDrawingsList() {
  const listContainer = $('#drawingsList');

  if (drawnShapes.length === 0) {
    listContainer.html(`
      <div style="text-align:center;color:#6c757d;padding:14px;">
        <i class="fas fa-draw-polygon" style="font-size:1.6rem;margin-bottom:8px;opacity:.5;"></i>
        <p style="margin:0;font-size:.8rem;">لا توجد رسومات بعد.</p>
      </div>
    `);
    return;
  }

  let html = '';
  drawnShapes.forEach((drawing, index) => {
    html += `
      <div class="drawing-item-modern">
        <div class="drawing-header">
          <span class="drawing-type">${drawing.type} ${index + 1}</span>
          <div class="drawing-actions">
            <button class="action-btn zoom-btn" data-id="${drawing.id}" title="تكبير"><i class="fas fa-search-plus"></i></button>
            <button class="action-btn delete-btn" data-id="${drawing.id}" title="حذف"><i class="fas fa-times"></i></button>
          </div>
        </div>
      </div>
    `;
  });

  listContainer.html(html);

  // ⚡ Level 3: Event Delegation for drawings actions
  listContainer.off('click').on('click', '.action-btn', function (e) {
    const btn = $(this);
    const id = parseInt(btn.data('id'));
    const target = $(e.target).closest('.action-btn');
    if (target.hasClass('zoom-btn')) {
      zoomToDrawing(id);
    } else if (target.hasClass('delete-btn')) {
      removeDrawing(id);
    }
  });
}

window.removeDrawing = function (id) {
  const idx = drawnShapes.findIndex(d => d.id === id);
  if (idx !== -1) {
    drawSource.removeFeature(drawnShapes[idx].feature);
    drawnShapes.splice(idx, 1);
    updateDrawingsList();
  }
};

window.zoomToDrawing = function (id) {
  const drawing = drawnShapes.find(d => d.id === id);
  if (drawing && drawing.feature) {
    const extent = drawing.feature.getGeometry().getExtent();
    map.getView().fit(extent, { duration: 500, padding: [40, 40, 40, 40], maxZoom: 11 });
  }
};

function clearDrawings() {
  drawSource.clear();
  drawnShapes = [];
  updateDrawingsList();
  removeDrawInteraction();
}

function exportDrawings() {
  if (drawnShapes.length === 0) {
    $("#errorMessage").html(`
      <div style="text-align:right;direction:rtl;">
        <h4 style="color:#dc3545;margin-bottom:10px;">
          <i class="fas fa-exclamation-triangle"></i> لا توجد رسومات
        </h4>
        <p style="margin:0 0 8px;line-height:1.5;">لا توجد رسومات لتصديرها. يرجى رسم بعض الأشكال أولاً.</p>
      </div>
    `);
    $("#errorModal").show();
    return;
  }

  const features = drawSource.getFeatures();
  const geojson = new GeoJSON().writeFeatures(features, { featureProjection: map.getView().getProjection() });

  const blob = new Blob([geojson], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `drawings_${new Date().toISOString().split('T')[0]}.geojson`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* =========================
   مودال النص
   ========================= */
function createTextModal() {
  if (document.getElementById('textModal')) return;
  const modalHTML = `
    <div id="textModal" class="coordinate-modal" style="display:none;">
      <div class="coordinate-modal-content" style="max-width:340px;">
        <div class="coordinate-modal-header">
          <h4><i class="fas fa-font"></i> إضافة نص</h4>
          <button class="coordinate-modal-close" id="closeTextModal" aria-label="إغلاق">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="coordinate-modal-body">
          <div class="coordinate-input-group">
            <label for="textInput" style="font-size:.9rem">النص المراد إضافته</label>
            <textarea id="textInput" placeholder="اكتب النص هنا..." class="coordinate-input" style="height:80px;resize:vertical;direction:rtl;text-align:right;font-size:.9rem;padding:10px;"></textarea>
          </div>
          <div class="coordinate-input-group">
            <label for="fontSize" style="font-size:.9rem">حجم الخط</label>
            <select id="fontSize" class="coordinate-input" style="padding:8px 10px;font-size:.9rem;">
              <option value="12">صغير (12px)</option>
              <option value="14" selected>متوسط (14px)</option>
              <option value="16">كبير (16px)</option>
              <option value="18">كبير جدًا (18px)</option>
              <option value="20">عملاق (20px)</option>
            </select>
          </div>
        </div>
        <div class="coordinate-modal-footer">
          <button class="coordinate-btn coordinate-btn-secondary" id="cancelTextBtn"><i class="fas fa-times"></i> إلغاء</button>
          <button class="coordinate-btn coordinate-btn-primary"  id="addTextBtn"><i class="fas fa-plus"></i> إضافة</button>
        </div>
      </div>
    </div>
  `;
  $('body').append(modalHTML);
  $('#closeTextModal, #cancelTextBtn').on('click', hideTextModal);
  $('#addTextBtn').on('click', handleAddText);
  $('#textModal').on('click', function (e) { if (e.target === this) hideTextModal(); });
}

function showTextModal(feature) {
  pendingTextFeature = feature;
  createTextModal();
  $('#textModal').css('display', 'flex');
  setTimeout(() => document.getElementById('textInput').focus(), 30);
}

function hideTextModal() {
  $('#textModal').hide();
  if (pendingTextFeature) { drawSource.removeFeature(pendingTextFeature); pendingTextFeature = null; }
}

function handleAddText() {
  const textInput = document.getElementById('textInput');
  const fontSize = document.getElementById('fontSize');
  const userText = (textInput.value || '').trim();

  if (!userText) {
    $("#errorMessage").html(`
      <div style="text-align:right;direction:rtl;">
        <h4 style="color:#dc3545;margin-bottom:10px;"><i class="fas fa-exclamation-triangle"></i> خطأ في الإدخال</h4>
        <p style="margin:0 0 8px;line-height:1.5;">يرجى كتابة النص أولًا قبل الإضافة.</p>
      </div>
    `);
    $("#errorModal").show();
    textInput.focus();
    return;
  }

  if (pendingTextFeature) {
    const strokeColor = document.getElementById('strokeColor')?.value || '#dc3545';
    const selectedFontSize = fontSize.value + 'px';

    pendingTextFeature.set('label', userText);
    pendingTextFeature.set('fontSize', selectedFontSize);
    pendingTextFeature.setStyle(new Style({
      text: new Text({
        text: userText,
        font: `bold ${selectedFontSize} Arial`,
        fill: new Fill({ color: strokeColor }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        textAlign: 'center',
        textBaseline: 'middle'
      })
    }));

    const id = Date.now();
    pendingTextFeature.setId(id);
    drawnShapes.push({ id, feature: pendingTextFeature, type: 'نص' });
    updateDrawingsList();
    pendingTextFeature = null;
  }
  hideTextModal();
}

/* =========================
   أدوات مساعدة
   ========================= */
function updatePreviewAndSave() {
  const stroke = document.getElementById('strokeColor')?.value || '#dc3545';
  const fill = document.getElementById('fillColor')?.value || '#dc3545';
  const width = document.getElementById('strokeWidth')?.value || '2';

  const preview = document.getElementById('stylePreview');
  if (preview) {
    preview.style.borderColor = stroke;
    preview.style.borderWidth = width + 'px';
    preview.style.background = `linear-gradient(135deg, ${hexToRgba(fill, 0.28)}, ${hexToRgba(fill, 0.12)})`;
  }

  const strokeDisplay = document.getElementById('strokeDisplay');
  const fillDisplay = document.getElementById('fillDisplay');
  const thicknessValue = document.getElementById('thicknessValue');

  if (strokeDisplay) { strokeDisplay.style.backgroundColor = stroke; strokeDisplay.textContent = stroke.toUpperCase(); }
  if (fillDisplay) { fillDisplay.style.backgroundColor = fill; fillDisplay.textContent = fill.toUpperCase(); }
  if (thicknessValue) { thicknessValue.textContent = width + 'px'; }

  const rangeInput = document.getElementById('strokeWidth');
  if (rangeInput) rangeInput.style.background = `linear-gradient(to right, #e9ecef 0%, ${stroke} 100%)`;

  localStorage.setItem('strokeColor', stroke);
  localStorage.setItem('fillColor', fill);
  localStorage.setItem('strokeWidth', width);
}

function hexToRgba(hex, alpha = 0.24) {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ✅ [INTERACTION VACUUM] - Lifecycle cleanup when switching tools
$(document).on('tool:cleanup', (e, data) => {
  if (data.id !== 'draw') {
    hideDrawMagnifier();
    // Stop any active drawing interaction
    if (isDrawing) {
      removeDrawInteraction();
      isDrawing = false;
      $('#draw').removeClass('active');
    }
  }
});

/* =========================
   تصدير
   ========================= */

// Expose to window for global access
window.updateCenterMagnifier = updateCenterMagnifier;

export { drawSource, drawLayer };
